import React from 'react'
import { Link } from 'react-router-dom'
import { fetchCurrentSubscription } from '../../lib/billingApi'

type Status = 'polling' | 'success' | 'timeout'

const MAX_ATTEMPTS = 10
const POLL_INTERVAL_MS = 2000

export function SuccessPage() {
  const [status, setStatus] = React.useState<Status>('polling')
  const [planName, setPlanName] = React.useState<string>('')

  React.useEffect(() => {
    const stored = localStorage.getItem('nudge-theme')
    if (stored === 'light' || stored === 'dark') {
      document.documentElement.setAttribute('data-theme', stored)
    }
  }, [])

  React.useEffect(() => {
    let cancelled = false
    let attempts = 0

    async function check() {
      if (cancelled) return
      attempts++
      try {
        const data = await fetchCurrentSubscription()
        if (data.subscription?.status === 'ACTIVE') {
          if (!cancelled) {
            setPlanName(data.plan.displayName)
            setStatus('success')
          }
          return
        }
      } catch {
        // keep trying
      }
      if (attempts >= MAX_ATTEMPTS) {
        if (!cancelled) setStatus('timeout')
        return
      }
      setTimeout(check, POLL_INTERVAL_MS)
    }

    check()
    return () => { cancelled = true }
  }, [])

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: 24,
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '40px 36px',
        maxWidth: 440,
        width: '100%',
        textAlign: 'center',
      }}>
        {status === 'polling' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Confirming your subscription…
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              This usually takes a few seconds. Hang tight.
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              You&apos;re on the {planName} plan. Welcome aboard!
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 28 }}>
              Every paid plan includes the full nudge sequence, Conversation-Aware AI, and recovery alerts.
            </div>
            <Link
              to="/dashboard"
              style={{
                display: 'inline-block',
                padding: '10px 28px',
                borderRadius: 8,
                background: 'var(--accent)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Go to dashboard →
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Payment received
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 28 }}>
              Your account will update shortly — refresh in a minute if your plan hasn't changed.
            </div>
            <Link
              to="/dashboard"
              style={{
                display: 'inline-block',
                padding: '10px 28px',
                borderRadius: 8,
                background: 'var(--accent)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Go to dashboard →
            </Link>
          </>
        )}

        <div style={{ marginTop: 20 }}>
          <a
            href="/dashboard"
            style={{ fontSize: 12, color: 'var(--text2)', textDecoration: 'none' }}
          >
            ← Back to dashboard
          </a>
        </div>
      </div>
    </div>
  )
}
